import type { ButtonHTMLAttributes, FC, ReactNode } from 'react'

import Link from 'next/link'
import { cn } from '@/lib/utils'
import { RollText } from '@/components/site/roll-text'

export type ButtonVariant = 'primary' | 'secondary' | 'ghost'
export type ButtonSize = 'sm' | 'md' | 'lg'

interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
    label: string
    href?: string
    variant?: ButtonVariant
    size?: ButtonSize
    children?: ReactNode
}

const base = 'roll-trigger relative isolate inline-flex shrink-0 select-none items-center justify-center overflow-clip font-sans font-semibold uppercase leading-[0.9] tracking-normal transition-transform duration-fast ease-studio active:scale-[0.98] disabled:pointer-events-none disabled:opacity-50 aria-disabled:pointer-events-none aria-disabled:opacity-50'

const overlay = 'before:absolute before:inset-0 before:-z-10 before:opacity-0 before:transition-opacity before:duration-fast before:ease-studio hover:before:opacity-100 focus-visible:before:opacity-100'

const variantClasses: Record<ButtonVariant, string> = {
    primary: cn('bg-primary text-primary-foreground before:bg-paper/10', overlay),
    secondary: cn('text-foreground ring-1 ring-inset ring-foreground/30 before:bg-foreground/[0.06]', overlay),
    ghost: cn('text-foreground before:bg-foreground/[0.06]', overlay)
}

const sizeClasses: Record<ButtonSize, string> = {
    sm: 'h-10 gap-3 px-4 pb-2 pt-[10px] text-[18px]',
    md: 'h-14 gap-4 px-6 pb-2.5 pt-3 text-[24px]',
    lg: 'h-20 gap-4 px-10 py-6 text-[32px]'
}

export const buttonClasses = (variant: ButtonVariant = 'primary', size: ButtonSize = 'md', className?: string): string => (
    cn(base, variantClasses[variant], sizeClasses[size], className)
)

export const Button: FC<ButtonProps> = ({
    label,
    href,
    variant = 'primary',
    size = 'md',
    className,
    children,
    type = 'button',
    ...rest
}): ReactNode => {
    const classes = buttonClasses(variant, size, className)
    const content = children ?? <RollText text={label} />

    if (href) {
        const external = /^(https?:|mailto:)/.test(href)

        if (external) {
            return (
                <a
                    href={href}
                    target={href.startsWith('http') ? '_blank' : undefined}
                    rel={href.startsWith('http') ? 'noopener noreferrer' : undefined}
                    aria-label={children ? label : undefined}
                    className={classes}
                >
                    {content}
                </a>
            )
        }

        return (
            <Link href={href} aria-label={children ? label : undefined} className={classes}>
                {content}
            </Link>
        )
    }

    return (
        <button
            type={type}
            aria-label={children ? label : undefined}
            className={classes}
            {...rest}
        >
            {content}
        </button>
    )
}
